"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useCart } from "@/src/contexts/CartContext";
import { toast } from "sonner";
import { SafeImage } from "@/src/components/SafeImage";
import { mapProduct } from "@/src/utils/products/mapProducts";
import { isProductSoldOut } from "@/src/utils/products/stock";
import { VariantPickerModal } from "./VariantPickerModal";
import { DeliveryEstimate } from "./DeliveryEstimate";
import { OutletLocation, hasMultipleOutlets } from "./OutletLocation";
import type { Product } from "@/src/types/tenant";
import type { IProduct } from "@/src/types/product";

interface BestsellersRailProps {
  products?: Product[];
  outletId?: string;
  prefix?: string;
  limit?: number;
  heading?: string;
  eyebrow?: string;
  /** Store spans several outlets — see AllProductsProps.multiOutlet. */
  multiOutlet?: boolean;
}

const inr = (n: number) => "₹" + n.toLocaleString("en-IN");

/**
 * Horizontal "Most loved" rail with one-tap add — drives add-to-cart and
 * product discovery from the top of the page. Bestsellers are derived from
 * real signals (biggest discount first, then the rest) — no fabricated
 * "bought this week" numbers.
 */
export const BestsellersRail: React.FC<BestsellersRailProps> = ({
  products = [],
  outletId = "",
  prefix = "",
  limit = 10,
  heading,
  eyebrow,
  multiOutlet,
}) => {
  const { addItem, getItemQuantity } = useCart();
  const [adding, setAdding] = useState<string | null>(null);
  const [pickerProduct, setPickerProduct] = useState<IProduct | null>(null);

  const ranked: IProduct[] = React.useMemo(() => {
    const mapped = products.map((p) => mapProduct(p, outletId));
    // Sold-out sinks below every in-stock item, then best discount leads.
    // Ordered before `limit` so the rail doesn't spend its slots advertising
    // deals nobody can buy.
    const soldOutRank = (p: IProduct) => (isProductSoldOut(p) ? 1 : 0);
    return [...mapped]
      .sort(
        (a, b) =>
          soldOutRank(a) - soldOutRank(b) ||
          (b.discountPct ?? 0) - (a.discountPct ?? 0)
      )
      .slice(0, limit);
  }, [products, outletId, limit]);

  /* See AllProducts — store-level truth wins; the rail's own list is only a
   * fallback for callers that don't pass it. */
  const showOutlet = React.useMemo(
    () => multiOutlet ?? hasMultipleOutlets(ranked),
    [multiOutlet, ranked]
  );

  /* Two is enough to read as a rail. Was three, which hid the section entirely
   * for a branch carrying only a couple of products — visible on Chak & Go once
   * the catalogue resolved to one outlet instead of showing both outlets' copies. */
  if (ranked.length < 2) return null;

  /* Carries the product's OWN outlet, not the storefront default — the PDP
   * prices and adds to cart against whatever outletId it receives. */
  const productHref = (p: IProduct) =>
    `/shop/product/${p.id}?prefix=${prefix}&outletId=${p.outletId}`;

  const quickAdd = async (p: IProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    // See AllProducts.handleAdd — the guard lives here too, not just in JSX.
    if (isProductSoldOut(p)) return;
    if (!p.tenantId) return;
    setAdding(p.id);
    try {
      await addItem({
        productId: p.id,
        outletId: p.outletId,
        tenantId: p.tenantId,
        name: p.name,
        price: p.price,
        mrp: p.originalPrice,
        quantity: 1,
        image: p.image,
      });
      toast.success(`Added ${p.name} to cart`);
    } catch (err) {
      toast.error((err as Error)?.message || "Couldn't add to cart");
    } finally {
      setAdding(null);
    }
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      <div className="mb-[clamp(16px,2vw,22px)] flex items-end justify-between gap-4">
        <div>
          <span className="mb-2.5 inline-flex items-center gap-2 text-[12px] font-bold uppercase" style={{ color: "var(--coral-deep)", letterSpacing: "0.1em" }}>
            <span style={{ width: 18, height: 9, borderRadius: "999px 999px 0 0", background: "var(--coral)", display: "inline-block" }} />
            {eyebrow || "Most loved"}
          </span>
          <h2 className="leading-tight" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(20px,2.6vw,30px)", letterSpacing: "-0.01em" }}>
            {heading || "Bestsellers this week"}
          </h2>
        </div>
        <a
          href={`/shop/all-products?prefix=${prefix}`}
          className="flex shrink-0 items-center gap-1.5 text-sm font-bold"
          style={{ color: "var(--navy)" }}
        >
          View all
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </a>
      </div>

      <div
        className="gl-rail flex gap-4 overflow-x-auto pb-2"
        style={{ scrollSnapType: "x proximity", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {ranked.map((p) => {
          const hasImage = p.image.startsWith("http");
          const hasVariants = (p.variantCount ?? 0) >= 2;
          const inCart = getItemQuantity(p.id) > 0;
          const lowStock = p.stock !== undefined && p.stock > 0 && p.stock <= 5;
          const soldOut = isProductSoldOut(p);
          return (
            <div
              key={p.id}
              className="group relative flex w-[200px] shrink-0 cursor-pointer flex-col overflow-hidden"
              style={{ scrollSnapAlign: "start", background: "var(--surface)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", padding: 10, boxShadow: "var(--shadow-soft)", transition: "transform .22s, box-shadow .22s" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "var(--shadow-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-soft)"; }}
            >
              {/* Stretched link — see AllProducts for why this is an overlay. */}
              <Link href={productHref(p)} aria-label={p.name} className="absolute inset-0 z-[1]" />
              <div className="relative overflow-hidden" style={{ aspectRatio: "1", borderRadius: "var(--radius)", background: "var(--cream)" }}>
                {hasImage ? (
                  <SafeImage src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" style={soldOut ? { opacity: 0.45 } : undefined} />
                ) : (
                  <div className="flex h-full items-center justify-center" style={{ fontFamily: "var(--font-display)", fontSize: 40, color: "rgba(9,42,76,0.16)" }}>
                    {p.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                {soldOut && (
                  <span
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-normal"
                    style={{ background: "rgba(255,255,255,0.94)", color: "var(--techno-black-70)", boxShadow: "0 2px 10px rgba(40,30,15,0.14)" }}
                  >
                    Sold out
                  </span>
                )}
                <div className="absolute left-2 top-2 flex flex-col gap-1">
                  {p.discountPct != null && p.discountPct >= 5 && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: "var(--coral)" }}>{p.discountPct}% off</span>
                  )}
                  {lowStock && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.92)", color: "var(--coral-deep)" }}>Only {p.stock} left</span>
                  )}
                </div>
              </div>

              <div className="flex flex-1 flex-col px-1 pt-2.5">
                {p.category && (
                  <span className="truncate text-[10px] font-bold uppercase" style={{ color: "var(--techno-black-50)", letterSpacing: "0.04em" }}>{p.category}</span>
                )}
                <h3 className="mt-0.5 leading-snug" style={{ fontWeight: 600, fontSize: 13.5, color: "var(--techno-black)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 36 }}>{p.name}</h3>

                {/* Delivery band for the outlet holding THIS product, not the
                    storefront default — a multi-branch seller has a different
                    band per card. See AllProducts for the same fix. */}
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <DeliveryEstimate outletId={p.outletId} />
                  {showOutlet && <OutletLocation city={p.outletCity} outletName={p.outletName} />}
                </div>

                <div className="mt-auto flex items-end justify-between gap-2 pt-2">
                  <div className="flex items-baseline gap-1.5">
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--techno-black)" }}>{inr(p.price)}</span>
                    {p.originalPrice && <s className="text-[11.5px]" style={{ color: "var(--techno-black-50)" }}>{inr(p.originalPrice)}</s>}
                  </div>
                  {soldOut ? (
                    <span
                      aria-label="Out of stock"
                      className="relative z-[2] flex h-9 shrink-0 cursor-not-allowed items-center rounded-full px-3 text-[12px] font-bold"
                      style={{ border: "1.5px solid var(--border)", color: "var(--techno-black-50)", background: "var(--surface-2)" }}
                    >
                      Sold out
                    </span>
                  ) : hasVariants ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPickerProduct(p); }}
                      aria-label={`Choose options for ${p.name}`}
                      className="relative z-[2] flex h-9 shrink-0 items-center rounded-full px-3 text-[12px] font-bold transition active:scale-95"
                      style={{ border: "1.5px solid var(--coral)", color: "var(--coral)", background: "var(--surface)" }}
                    >
                      Options
                    </button>
                  ) : (
                    <button
                      onClick={(e) => quickAdd(p, e)}
                      disabled={adding === p.id}
                      aria-label={`Add ${p.name} to cart`}
                      className="relative z-[2] flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition active:scale-95 disabled:opacity-60"
                      style={{ background: inCart ? "var(--coral-soft)" : "var(--coral)", color: inCart ? "var(--coral-deep)" : "#fff" }}
                    >
                      {inCart ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`.gl-rail::-webkit-scrollbar{display:none}`}</style>

      {/* Inline variant picker — opens when "Options" is tapped on a product with variants */}
      {pickerProduct && (
        <VariantPickerModal
          product={pickerProduct}
          onClose={() => setPickerProduct(null)}
        />
      )}
    </section>
  );
};
